import { Link, useNavigate } from "react-router-dom";
import styled from "styled-components";
import {
    openPasswordPrompt,
    useHasSetPasswordLocally,
} from "src/Module/Checkout/signupPasswordPrompt";

import {
    StyledHeader,
    IconCart,
    IconUser,
    MenuHeaderComponent,
    IconFileText,
    IconLock,
    StyledButton,
    IconSearch,
    HeaderNavigatorComponent,
} from "@components";
import {
    PATH_CART,
    PATH_HOME,
    PATH_BOOKING,
    PATH_QUOTES,
    PATH_MY_TRIPS,
    PATH_PROFILE,
    PATH_UPDATE_PASSWORD,
    PATH_SEARCH_RESULT,
    PATH_LINK_AGENT_ACCOUNT,
    PATH_POINTS_HISTORY,
    PATH_LOGIN,
    PATH_TERM,
} from "@routes";
import {
    resetSearch,
    selectAccount,
    selectAuth,
    selectCmsHeaderMenu,
    useAppSelector,
} from "@redux";
import {
    breakpoint,
    sessionHelper,
    theme,
    useWindowDimensions,
    isBottomBarVisible as checkBottomBarVisible,
} from "@utilities";
import { useEffect, useState } from "react";
import _ from "lodash";
import moment from "moment";
import { LocationArea } from "./LocationArea";
import { useDispatch } from "react-redux";
import { Z_INDEX } from "@constants";

export interface IHeaderComponent {
    page?: string;
    $isScrollUp?: boolean;
    $headerFix?: boolean;
    $toggleShowMenu: () => void;
    $isShowMenu: boolean;
    $productCount: number;
    $isCartLoading?: boolean;
}

const getCartUrl = (): string => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlSessionId = urlParams.get("sessionId");
    if (urlSessionId) return `${PATH_CART}?sessionId=${urlSessionId}`;
    if (sessionHelper.hasSession()) return `${PATH_CART}?sessionId=${sessionHelper.getSessionId()}`;
    return PATH_CART;
};

export const HeaderComponent = (props: IHeaderComponent) => {
    const { $toggleShowMenu, $isShowMenu, $productCount, $isScrollUp, $headerFix, $isCartLoading } =
        props;
    const { jwt } = useAppSelector(selectAuth);
    const { account, metadata } = useAppSelector(selectAccount);
    const [isSignedIn, setIsSignedIn] = useState(!_.isNil(jwt) && !_.isNil(account));
    /**
     * Whether to offer the "Set password" prompt.
     *
     * Three conditions, because the backend flag on its own has proved able to
     * stay set after a password really was chosen:
     *
     * - the account says its password was generated, which is the real signal;
     * - it signs in by email — a Google or Apple account authenticates through
     *   the provider and has no password to set, which is why the Update Password
     *   menu item is already hidden for them;
     * - and we have not set one for this account from this browser ourselves.
     */
    /**
     * Whether this account already earns, by either signal the app keeps.
     *
     * `account.is_agent_integrated` is what this header has always read, but the
     * checkout gates its points UI on the metadata flags the backend derives from
     * TDMS. They can disagree, and when they do the Upgrade button was offering an
     * upgrade to someone who already has one.
     *
     * `is_default_agent` matters as much as the other two: a PeterPans-branch
     * account often resolves in TDMS as a default agent rather than a points one,
     * so both points and commission read false while an agent record exists. The
     * upgrade then fails with "You already have an account with that email
     * address" — the button was offering something the backend would always
     * refuse.
     */
    const isAlreadyAgent = Boolean(
        account?.is_agent_integrated ||
        metadata?.is_points_agent ||
        metadata?.is_commission_agent ||
        metadata?.is_default_agent,
    );

    const hasSetPassword = useHasSetPasswordLocally(account?.email);
    const needsPasswordSetup =
        Boolean(account?.is_password_randomly_generated) &&
        account?.sso_type === "email" &&
        !hasSetPassword;
    const {
        data: pointsData,
        loading: pointsLoading,
        refreshing: pointsRefreshing,
        commissionHistory,
    } = useAppSelector((state) => state.points);
    const pathname = window.location.pathname;
    const isHome = pathname === PATH_HOME;
    const { width } = useWindowDimensions();

    // Check if bottom bar is visible (mobile only)
    const isBottomBarVisible = checkBottomBarVisible(pathname, width);

    const isLight = $isScrollUp || !$headerFix;
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const handleSearchClick = () => {
        dispatch(resetSearch());
        navigate(PATH_HOME);
    };

    useEffect(() => {
        setIsSignedIn(!_.isNil(jwt) && !_.isNil(account));
    }, [jwt, account]);

    const isOrderTabEnabled = Boolean(metadata?.is_order_tab_enabled);

    // Calculate commission amounts
    const today = moment();

    // Sum of commissions that are still pending (availableDate is after today)
    // These are commissions that haven't been paid out yet
    const totalCommissionPending = (commissionHistory || [])
        .filter((item) => moment(item.availableDate).isAfter(today, "day"))
        .reduce((sum, item) => sum + parseFloat(item.commission), 0);

    // Sum of all commissions earned from bookings (both pending and available)
    // This represents the total commission amount earned regardless of payout status
    const totalCommissionEarned = (commissionHistory || []).reduce(
        (sum, item) => sum + parseFloat(item.commission),
        0,
    );

    const listMenuItems = [
        {
            icon: IconUser,
            title: "Profile",
            link: PATH_PROFILE,
        },
        {
            icon: IconFileText,
            title: "My Bookings",
            link: PATH_BOOKING,
        },
        {
            icon: IconFileText,
            title: "My Trips",
            link: PATH_MY_TRIPS,
        },
        {
            icon: IconCart,
            title: account?.is_agent_integrated
                ? "Orders / Quotes"
                : isOrderTabEnabled
                  ? "Orders"
                  : "Quotes",
            link: PATH_QUOTES,
        },
        {
            icon: IconLock,
            title: needsPasswordSetup ? "Set Password" : "Update Password",
            link: PATH_UPDATE_PASSWORD,
            // The Update Password page asks for the current password, which an
            // account created with a generated one was never shown — so this opens
            // the modal that can prove identity another way instead.
            onClick: needsPasswordSetup ? openPasswordPrompt : undefined,
            alert: needsPasswordSetup,
        },
    ];

    return (
        <>
            {isHome &&
                isSignedIn &&
                account?.is_points_displayed &&
                account?.is_agent_integrated &&
                pointsData && (
                    <TopBanner onClick={() => navigate(PATH_POINTS_HISTORY)}>
                        <TopBannerContent>
                            {metadata?.is_commission_agent && (
                                <img
                                    src="/images/twemoji_coin.svg"
                                    alt="coin"
                                    style={{ width: 18, height: 18 }}
                                />
                            )}
                            {pointsLoading || pointsRefreshing ? (
                                <span>Loading...</span>
                            ) : metadata?.is_commission_agent && pointsData.pointsMultiplier > 0 ? (
                                <span>
                                    Commission Owed: ${totalCommissionPending.toFixed(2)} | Total
                                    Earned: ${totalCommissionEarned.toFixed(2)}
                                </span>
                            ) : (
                                <span>
                                    Total: {pointsData.pointsBalance} pts | Available:{" "}
                                    {pointsData.availableBalance} pts | Pending:{" "}
                                    {(
                                        pointsData.pointsBalance - pointsData.availableBalance
                                    ).toFixed(2)}{" "}
                                    pts
                                </span>
                            )}
                            <img
                                src="/images/chevron-right.svg"
                                alt="arrow"
                                style={{ width: 18, height: 18 }}
                            />
                        </TopBannerContent>
                    </TopBanner>
                )}

            <StyledHeader
                {...props}
                $hasTopBanner={
                    isHome &&
                    isSignedIn &&
                    account?.is_points_displayed &&
                    account?.is_agent_integrated &&
                    !!pointsData
                }
            >
                <div className="backgroundHeader"></div>
                <MainContainer>
                    <div className="wrapper">
                        {/* The shop's own homepage, not the brand's marketing site:
                            leaving mid-booking dropped the cart and the checkout. */}
                        <Link className="logo" to={PATH_HOME}>
                            <Logo
                                src="/images/logos/Primary_logo_white.svg"
                                alt="logo"
                                className="logoItem logoWhite"
                            />
                            <Logo
                                src="/images/logos/Primary_logo_color.svg"
                                alt="logo"
                                className="logoItem logoColor"
                            />
                        </Link>

                        {!isSignedIn ? (
                            <ButtonWrapper>
                                <LocationContainerDesktop>
                                    <LocationArea
                                        $isLight={isLight}
                                        $productCount={$productCount}
                                    />
                                </LocationContainerDesktop>
                                <div style={{ display: "flex", gap: "20px" }} className="cart">
                                    <div className="cartIconWrapper">
                                        <Link
                                            to={getCartUrl()}
                                            style={{
                                                pointerEvents: $isCartLoading ? "none" : "auto",
                                                opacity: $isCartLoading ? 0.5 : 1,
                                            }}
                                            data-gtm-id="view-cart"
                                        >
                                            {$isCartLoading ? (
                                                <CartLoadingSpinner />
                                            ) : (
                                                $productCount > 0 && (
                                                    <span className="number">{$productCount}</span>
                                                )
                                            )}
                                            <IconCart />
                                        </Link>
                                    </div>
                                    <HamburgerWrapper onClick={$toggleShowMenu}>
                                        <HamburgerIcon $isLight={!isLight}>
                                            <span></span>
                                            <span></span>
                                            <span></span>
                                        </HamburgerIcon>
                                        {$isShowMenu && <OverLay />}
                                        {$isShowMenu && (
                                            <GuestMenu
                                                onClose={$toggleShowMenu}
                                                productCount={$productCount}
                                            />
                                        )}
                                    </HamburgerWrapper>
                                </div>
                            </ButtonWrapper>
                        ) : (
                            <div className="cart">
                                {/* Deliberately not gated on is_points_displayed, unlike
                                    every other points surface: that flag is the
                                    visitor's own show/hide preference for points they
                                    already earn, and someone who is not an agent yet has
                                    none to hide. Reading it here suppressed the one
                                    invitation to start earning. */}
                                {isHome && !isAlreadyAgent && (
                                    <UpgradeButton
                                        onClick={() => navigate(PATH_LINK_AGENT_ACCOUNT)}
                                        $borderRadius="50px"
                                        color={theme.gradients.upgradeButton}
                                    >
                                        Upgrade
                                    </UpgradeButton>
                                )}

                                <LocationContainerDesktop>
                                    <LocationArea
                                        $isLight={isLight}
                                        $productCount={$productCount}
                                    />
                                </LocationContainerDesktop>
                                {!pathname.includes(PATH_HOME && PATH_SEARCH_RESULT) && (
                                    <div className="searchIconWrapper">
                                        <div
                                            onClick={handleSearchClick}
                                            className="cartIconWrapper iconWrapper"
                                        >
                                            <IconSearch />
                                        </div>
                                    </div>
                                )}
                                <div className="cartIconWrapper">
                                    <Link
                                        to={getCartUrl()}
                                        style={{
                                            pointerEvents: $isCartLoading ? "none" : "auto",
                                            opacity: $isCartLoading ? 0.5 : 1,
                                        }}
                                        data-gtm-id="view-cart"
                                    >
                                        {$isCartLoading ? (
                                            <CartLoadingSpinner />
                                        ) : (
                                            $productCount > 0 && (
                                                <span className="number">{$productCount}</span>
                                            )
                                        )}
                                        <IconCart />
                                    </Link>
                                </div>
                                {!isBottomBarVisible && (
                                    <div className="userIconWrapper" onClick={$toggleShowMenu}>
                                        {isSignedIn && <IconUser />}
                                        {/* Flags a password generated at sign-up and
                                            never replaced. The menu this icon opens
                                            carries the Set Password entry. */}
                                        {isSignedIn && needsPasswordSetup && (
                                            <span
                                                className="passwordAlert"
                                                title="Set a password for your account"
                                                aria-label="Set a password for your account"
                                            >
                                                !
                                            </span>
                                        )}
                                        {$isShowMenu && <OverLay />}
                                        {$isShowMenu && (
                                            <MenuHeaderComponent listMenuItems={listMenuItems} />
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </MainContainer>
                <LocationContainerMobile>
                    <LocationArea $isLight={isLight} $productCount={$productCount} />
                </LocationContainerMobile>
            </StyledHeader>
        </>
    );
};

const MainContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 20px 16px 20px 16px;
`;

const OverLay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: transparent;
`;

const HamburgerWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    cursor: pointer;
    margin-left: 18px;

    ${breakpoint.breakTablet`
        margin-left: 30px;
    `}
`;

const HamburgerIcon = styled.div<{ $isLight: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 4px;
    cursor: pointer;

    span {
        width: 24px;
        height: 2px;
        background-color: ${({ $isLight }) => ($isLight ? "#fff" : "#000")};
        border-radius: 2px;
    }
`;

const ButtonWrapper = styled.div`
    display: flex;
    align-items: center;
    margin-left: auto;
    /* Holds the burger for signed-out visitors, so it must keep its width
       whatever the logo beside it wants. */
    flex-shrink: 0;

    ${breakpoint.breakIpadPro`
        width: auto;
    `}
    ${breakpoint.breakLarge`
        width: auto;
    `}
`;

const UpgradeButton = styled(StyledButton)`
    margin-top: 0px;
    font-size: 14px;
    font-family: Plus Jakarta Sans;
    font-weight: 600;
    height: 36px;
    padding: 0 24px;

    ${breakpoint.breakOnlyMobile`
        min-width: 100px;
    `}

    ${breakpoint.breakTablet`
        margin-right: 30px;
    `}
`;

const LocationContainerMobile = styled.div`
    display: flex;
    align-items: center;
    padding: 0 16px 8px 16px;
    ${breakpoint.breakMobileMedium`
        display: none;
    `}
    ${breakpoint.breakIpadPro`
        display: none;
    `}
    ${breakpoint.breakLarge`
        display: none;
    `}
`;

const LocationContainerDesktop = styled.div`
    display: none;
    align-items: center;
    justify-content: space-between;
    position: relative;
    margin-left: auto;

    ${breakpoint.breakMobileMedium`
        display: flex;
        max-width: calc(180px - 100px);
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    `}
    ${breakpoint.breakIpadPro`
        display: flex;
        max-width: calc(200px - 100px);
    `}
    ${breakpoint.breakLarge`
        display: flex;
        max-width: calc(280px - 100px);
    `}
`;

const Logo = styled.img`
    max-height: 61px;
    width: auto;
`;

const GuestMenu = ({ onClose, productCount }: { onClose: () => void; productCount: number }) => {
    const cmsHeaderMenu = useAppSelector(selectCmsHeaderMenu);
    const cmsMenuItems = cmsHeaderMenu?.items ?? [];
    return (
        <GuestMenuWrapper>
            <GuestMenuHeader>
                <HeaderNavigatorComponent $onBackToHome={onClose} $productCount={productCount} />
            </GuestMenuHeader>
            <GuestMenuContainer>
                <Link className="linkItem" to={PATH_LOGIN}>
                    <div className="itemWrapper">
                        <span className="icon">
                            <img src="/images/Log.svg" alt="terms" />
                        </span>
                        <span className="linkText">Login or Signup</span>
                    </div>
                </Link>
                {/* Backend-driven header menu items (per flavor); falls back to none if absent. */}
                {cmsMenuItems.map((item) =>
                    item.external ? (
                        <a
                            key={item.id}
                            className="linkItem"
                            href={item.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={onClose}
                        >
                            <div className="itemWrapper">
                                <span className="icon">
                                    <img src="/images/Frame.svg" alt={item.label} />
                                </span>
                                <span className="linkText">{item.label}</span>
                            </div>
                        </a>
                    ) : (
                        <Link key={item.id} className="linkItem" to={item.href} onClick={onClose}>
                            <div className="itemWrapper">
                                <span className="icon">
                                    <img src="/images/Frame.svg" alt={item.label} />
                                </span>
                                <span className="linkText">{item.label}</span>
                            </div>
                        </Link>
                    ),
                )}
                <Link className="linkItem" to={PATH_TERM}>
                    <div className="itemWrapper">
                        <span className="icon">
                            <img src="/images/Frame.svg" alt="terms" />
                        </span>
                        <span className="linkText">Terms and Conditions</span>
                    </div>
                </Link>
                <div className="linkItem">
                    <div className="itemWrapper">
                        <span className="icon">
                            <img src="/images/App.svg" alt="app" />
                        </span>
                        <span className="linkText">
                            Install{" "}
                            <AppLink
                                href="https://play.google.com/store/apps/details?id=com.peterpans.app"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Android
                            </AppLink>
                            {" | "}
                            <AppLink
                                href="https://apps.apple.com/us/app/peterpans-adventure-travel/id6748809454"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                iOS
                            </AppLink>
                            {" App"}
                        </span>
                    </div>
                </div>
            </GuestMenuContainer>
        </GuestMenuWrapper>
    );
};

const GuestMenuWrapper = styled.div`
    ${breakpoint.breakOnlyMobile`
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        width: 100%;
        height: 100vh;
        z-index: ${Z_INDEX.MODAL_CONTENT};
        background-color: #fff;
    `}
`;

const GuestMenuHeader = styled.div`
    display: none;

    ${breakpoint.breakOnlyMobile`
        display: block;
        box-shadow: 0px 0px 6px rgba(0, 0, 0, 0.15);
    `}
`;

const GuestMenuContainer = styled.div`
    width: 255px;
    background-color: #fff;
    position: absolute;
    top: calc(100% + 20px);
    right: 0;
    box-shadow: 4px 4px 45px rgba(0, 0, 0, 0.08);
    border: 1px solid ${(p) => p.theme.colors.gray_5};
    padding: 0 30px 0 16px;

    ${breakpoint.breakOnlyMobile`
        position: relative;
        top: 0;
        width: 100%;
        border: none;
        box-shadow: none;
        padding: 0 30px 30px 16px;
    `}

    .linkItem {
        cursor: pointer;

        .itemWrapper {
            display: flex;
            align-items: center;
            padding: 24px 0;
            border-bottom: 1px solid ${(p) => p.theme.colors.gray_5};
        }

        .icon {
            margin-right: 16px;

            svg {
                width: 20px;

                path {
                    stroke: ${(p) => p.theme.colors.gray_1} !important;
                }
            }

            img {
                width: 20px;
                height: 20px;
            }
        }

        .linkText {
            font-size: 16px;
            font-weight: 500;
            line-height: 21px;
            color: #000;
        }
    }
`;

const AppLink = styled.a`
    color: ${(props) => props.theme.colors.highlight};
    text-decoration: none;
    cursor: pointer;
    
    &:hover {
        text-decoration: underline;
    }

        &:last-child {
            .itemWrapper {
                border-bottom: none;
            }
        }
    }
`;

const CartLoadingSpinner = styled.div`
    position: absolute;
    top: -8px;
    right: -8px;
    width: 16px;
    height: 16px;
    border: 2px solid #f3f3f3;
    border-top: 2px solid ${theme.colors.highlight};
    border-radius: 50%;
    animation: spin 1s linear infinite;

    @keyframes spin {
        0% {
            transform: rotate(0deg);
        }
        100% {
            transform: rotate(360deg);
        }
    }
`;

const TopBanner = styled.div`
    width: 100%;
    height: 28px;
    background: ${(p) => p.theme.colors.main};
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    position: fixed;
    top: 0;
    left: 0;
    z-index: ${Z_INDEX.HEADER};
`;

const TopBannerContent = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    gap: 4px;

    span {
        font-family: "Poppins";
        font-style: normal;
        font-weight: 500;
        font-size: 12px;
        line-height: 120%;
        color: ${(p) => p.theme.colors.black};
        text-align: center;
    }

    /* The chevron is a fixed white stroke, so it disappeared against the yellow.
       Rendered black to match the text rather than shipping a second asset. The
       coin before it is a colour emoji and is deliberately left alone. */
    img:last-child {
        filter: brightness(0);
    }
`;
